import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import useWishlist from "../../hooks/useWishlist";
import { normalizeMaterialLabel } from "../../utils/materialLabels";
import { formatInr, getProductPriceDisplay } from "../../utils/pricing";
import { saveListingReturnPosition } from "../../utils/scrollRestoration";
import "./ProductCard.css";

const DEFAULT_MEDIA_FRAME_PRESET = "balanced";
const DEFAULT_MEDIA_SURFACE = "#ffffff";
const DEFAULT_MEDIA_FOCUS_POINT = { x: 50, y: 50 };
const DEFAULT_MEDIA_CONTENT_SCALE = 1;
const DEFAULT_MEDIA_FIT_MODE = "cover";
const DEFAULT_MEDIA_ASPECT_RATIO = 1;
const DEFAULT_MEDIA_FRAME_ASPECT_RATIO = 1;
const DEFAULT_MEDIA_PRESENTATION = {
  aspectRatio: DEFAULT_MEDIA_ASPECT_RATIO,
  frameAspectRatio: DEFAULT_MEDIA_FRAME_ASPECT_RATIO,
  framePreset: DEFAULT_MEDIA_FRAME_PRESET,
  surface: DEFAULT_MEDIA_SURFACE,
  focusX: DEFAULT_MEDIA_FOCUS_POINT.x,
  focusY: DEFAULT_MEDIA_FOCUS_POINT.y,
  contentScale: DEFAULT_MEDIA_CONTENT_SCALE,
  fitMode: DEFAULT_MEDIA_FIT_MODE,
};

const getMediaFramePreset = (width, height) => {
  const safeWidth = Number(width);
  const safeHeight = Number(height);

  if (!Number.isFinite(safeWidth) || !Number.isFinite(safeHeight) || safeWidth <= 0 || safeHeight <= 0) {
    return DEFAULT_MEDIA_FRAME_PRESET;
  }

  const aspectRatio = safeWidth / safeHeight;

  if (aspectRatio <= 0.84) {
    return "portrait";
  }

  if (aspectRatio >= 1.2) {
    return "landscape";
  }

  return DEFAULT_MEDIA_FRAME_PRESET;
};

const clampNumber = (value, min, max) => Math.max(min, Math.min(max, value));

const clampColorChannel = (value) => Math.round(clampNumber(value, 0, 255));

const toHexPair = (value) => clampColorChannel(value).toString(16).padStart(2, "0");

const rgbToHex = ({ r, g, b }) => `#${toHexPair(r)}${toHexPair(g)}${toHexPair(b)}`;

const isNearWhiteSurface = ({ r, g, b }) => {
  const luminance = (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
  const channelSpread = Math.max(r, g, b) - Math.min(r, g, b);

  return luminance >= 232 && channelSpread <= 18;
};

const getCanvasEdgeAverageColor = (imageData, width, height, edgeDepth) => {
  let red = 0;
  let green = 0;
  let blue = 0;
  let weight = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const isEdgePixel =
        x < edgeDepth
        || x >= width - edgeDepth
        || y < edgeDepth
        || y >= height - edgeDepth;

      if (!isEdgePixel) {
        continue;
      }

      const index = (y * width + x) * 4;
      const alpha = imageData[index + 3] / 255;

      if (alpha < 0.48) {
        continue;
      }

      const pixelWeight = alpha;
      red += imageData[index] * pixelWeight;
      green += imageData[index + 1] * pixelWeight;
      blue += imageData[index + 2] * pixelWeight;
      weight += pixelWeight;
    }
  }

  if (weight < 1) {
    return null;
  }

  return {
    r: red / weight,
    g: green / weight,
    b: blue / weight,
  };
};

const getMediaSurfaceColor = (imageElement) => {
  if (
    typeof document === "undefined"
    || !imageElement
    || !imageElement.naturalWidth
    || !imageElement.naturalHeight
  ) {
    return DEFAULT_MEDIA_SURFACE;
  }

  try {
    const sampleSize = 28;
    const edgeDepth = 4;
    const canvas = document.createElement("canvas");
    canvas.width = sampleSize;
    canvas.height = sampleSize;

    const context = canvas.getContext("2d", {
      willReadFrequently: true,
    });

    if (!context) {
      return DEFAULT_MEDIA_SURFACE;
    }

    context.drawImage(imageElement, 0, 0, sampleSize, sampleSize);

    const { data } = context.getImageData(0, 0, sampleSize, sampleSize);
    const averageColor = getCanvasEdgeAverageColor(data, sampleSize, sampleSize, edgeDepth);

    if (!averageColor) {
      return DEFAULT_MEDIA_SURFACE;
    }

    if (isNearWhiteSurface(averageColor)) {
      return DEFAULT_MEDIA_SURFACE;
    }

    return rgbToHex(averageColor);
  } catch {
    return DEFAULT_MEDIA_SURFACE;
  }
};

const getMediaContentFit = (imageElement) => {
  if (
    typeof document === "undefined"
    || !imageElement
    || !imageElement.naturalWidth
    || !imageElement.naturalHeight
  ) {
    return {
      focusX: DEFAULT_MEDIA_FOCUS_POINT.x,
      focusY: DEFAULT_MEDIA_FOCUS_POINT.y,
      contentScale: DEFAULT_MEDIA_CONTENT_SCALE,
      normalizedContentWidth: 1,
      normalizedContentHeight: 1,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      marginBottom: 0,
    };
  }

  try {
    const longestSide = 144;
    const imageAspectRatio = imageElement.naturalWidth / imageElement.naturalHeight;
    const sampleWidth =
      imageAspectRatio >= 1
        ? longestSide
        : Math.max(72, Math.round(longestSide * imageAspectRatio));
    const sampleHeight =
      imageAspectRatio >= 1
        ? Math.max(72, Math.round(longestSide / imageAspectRatio))
        : longestSide;
    const edgeDepth = Math.max(4, Math.round(Math.min(sampleWidth, sampleHeight) * 0.07));
    const canvas = document.createElement("canvas");
    canvas.width = sampleWidth;
    canvas.height = sampleHeight;

    const context = canvas.getContext("2d", {
      willReadFrequently: true,
    });

    if (!context) {
      return {
        focusX: DEFAULT_MEDIA_FOCUS_POINT.x,
        focusY: DEFAULT_MEDIA_FOCUS_POINT.y,
        contentScale: DEFAULT_MEDIA_CONTENT_SCALE,
        normalizedContentWidth: 1,
        normalizedContentHeight: 1,
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        marginBottom: 0,
      };
    }

    context.drawImage(imageElement, 0, 0, sampleWidth, sampleHeight);

    const { data } = context.getImageData(0, 0, sampleWidth, sampleHeight);
    const backgroundColor =
      getCanvasEdgeAverageColor(data, sampleWidth, sampleHeight, edgeDepth)
      || { r: 255, g: 255, b: 255 };
    const backgroundLuminance =
      (0.2126 * backgroundColor.r) + (0.7152 * backgroundColor.g) + (0.0722 * backgroundColor.b);
    let minX = sampleWidth;
    let minY = sampleHeight;
    let maxX = -1;
    let maxY = -1;
    let contentPixelCount = 0;

    for (let y = 0; y < sampleHeight; y += 1) {
      for (let x = 0; x < sampleWidth; x += 1) {
        const index = (y * sampleWidth + x) * 4;
        const alpha = data[index + 3] / 255;

        if (alpha < 0.08) {
          continue;
        }

        const red = data[index];
        const green = data[index + 1];
        const blue = data[index + 2];
        const colorDistance = Math.sqrt(
          ((red - backgroundColor.r) ** 2)
          + ((green - backgroundColor.g) ** 2)
          + ((blue - backgroundColor.b) ** 2)
        );
        const luminance =
          (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
        const luminanceDifference = Math.abs(luminance - backgroundLuminance);

        if (colorDistance < 18 && luminanceDifference < 12) {
          continue;
        }

        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
        contentPixelCount += 1;
      }
    }

    if (contentPixelCount < 24 || maxX < minX || maxY < minY) {
      return {
        focusX: DEFAULT_MEDIA_FOCUS_POINT.x,
        focusY: DEFAULT_MEDIA_FOCUS_POINT.y,
        contentScale: DEFAULT_MEDIA_CONTENT_SCALE,
        normalizedContentWidth: 1,
        normalizedContentHeight: 1,
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        marginBottom: 0,
      };
    }

    const normalizedContentWidth = (maxX - minX + 1) / sampleWidth;
    const normalizedContentHeight = (maxY - minY + 1) / sampleHeight;
    const marginLeft = minX / sampleWidth;
    const marginRight = 1 - ((maxX + 1) / sampleWidth);
    const marginTop = minY / sampleHeight;
    const marginBottom = 1 - ((maxY + 1) / sampleHeight);
    const focusX = clampNumber((((minX + maxX + 1) / 2) / sampleWidth) * 100, 8, 92);
    const focusY = clampNumber((((minY + maxY + 1) / 2) / sampleHeight) * 100, 8, 92);
    const contentScale = clampNumber(
      Math.max(1 / normalizedContentWidth, 1 / normalizedContentHeight),
      1.02,
      2.4
    );

    return {
      focusX,
      focusY,
      contentScale,
      normalizedContentWidth,
      normalizedContentHeight,
      marginLeft,
      marginRight,
      marginTop,
      marginBottom,
    };
  } catch {
    return {
      focusX: DEFAULT_MEDIA_FOCUS_POINT.x,
      focusY: DEFAULT_MEDIA_FOCUS_POINT.y,
      contentScale: DEFAULT_MEDIA_CONTENT_SCALE,
      normalizedContentWidth: 1,
      normalizedContentHeight: 1,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 0,
      marginBottom: 0,
    };
  }
};

const getProductCardFitMode = () => DEFAULT_MEDIA_FIT_MODE;

const normalizeDisplayText = (value = "") => String(value || "").trim();
const TITLE_CASE_UPPERCASE_WORDS = new Set([
  "CZ",
  "CVD",
  "LGD",
  "IGI",
  "GIA",
  "KDM",
  "NA",
  "N/A",
]);

const toTitleCaseSegment = (segment = "") => {
  if (!segment) {
    return "";
  }

  const upperSegment = segment.toUpperCase();

  if (TITLE_CASE_UPPERCASE_WORDS.has(upperSegment) || /\d/.test(segment)) {
    return upperSegment;
  }

  return `${segment.charAt(0).toUpperCase()}${segment.slice(1).toLowerCase()}`;
};

const toProductTitleCase = (value = "") =>
  normalizeDisplayText(value)
    .split(/(\s+|[-/&()])/)
    .map((segment) => (/^[A-Za-z0-9]+$/.test(segment) ? toTitleCaseSegment(segment) : segment))
    .join("");

const isMaterialOnlyTitle = (title = "", material = "") => {
  const normalizedTitle = normalizeMaterialLabel(title).toLowerCase();
  const normalizedMaterial = normalizeMaterialLabel(material).toLowerCase();

  return Boolean(normalizedTitle && normalizedMaterial && normalizedTitle === normalizedMaterial);
};

const getProductDisplayTitle = (product = {}) => {
  const title = normalizeDisplayText(product.title);
  const legacyName = normalizeDisplayText(product.name);

  if (legacyName && (!title || isMaterialOnlyTitle(title, product.material))) {
    return toProductTitleCase(legacyName);
  }

  return toProductTitleCase(title || legacyName || "Product");
};

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const [inWishlist, setInWishlist] = React.useState(false);
  const [mediaPresentation, setMediaPresentation] = React.useState(DEFAULT_MEDIA_PRESENTATION);
  const videoRef = React.useRef(null);
  const cardRef = React.useRef(null);
  const primaryMedia = product?.images?.[0];

  const isVideo = (url) => {
    if (!url) {
      return false;
    }

    const videoExtensions = [".mp4", ".avi", ".mov", ".wmv", ".flv", ".webm"];
    return videoExtensions.some((extension) => url.toLowerCase().includes(extension));
  };

  const safePlayVideo = async (videoElement) => {
    if (!videoElement) {
      return;
    }

    try {
      await videoElement.play();
    } catch (error) {
      console.log("Video play prevented:", error.message);
    }
  };

  const safePauseVideo = (videoElement) => {
    if (!videoElement) {
      return;
    }

    try {
      videoElement.pause();
    } catch (error) {
      console.log("Video pause prevented:", error.message);
    }
  };

  const cleanupVideo = React.useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    safePauseVideo(videoRef.current);
    videoRef.current = null;
  }, []);

  React.useEffect(() => {
    if (product && product._id) {
      setInWishlist(isInWishlist(product._id));
    }
  }, [product, isInWishlist]);

  React.useEffect(() => {
    return () => {
      cleanupVideo();
    };
  }, [cleanupVideo]);

  React.useEffect(() => {
    setMediaPresentation(DEFAULT_MEDIA_PRESENTATION);
  }, [primaryMedia]);

  const handlePrimaryMediaLoad = (event) => {
    const frameElement = event.currentTarget.parentElement;
    const frameWidth = frameElement?.clientWidth || 0;
    const frameHeight = frameElement?.clientHeight || 0;
    const imageAspectRatio =
      event.currentTarget.naturalWidth > 0 && event.currentTarget.naturalHeight > 0
        ? event.currentTarget.naturalWidth / event.currentTarget.naturalHeight
        : 1;
    const cardAspectRatio =
      frameWidth > 0 && frameHeight > 0
        ? frameWidth / frameHeight
        : imageAspectRatio;
    const nextFramePreset = getMediaFramePreset(
      event.currentTarget.naturalWidth,
      event.currentTarget.naturalHeight
    );
    const nextSurface = getMediaSurfaceColor(event.currentTarget);
    const nextContentFit = getMediaContentFit(event.currentTarget);

    setMediaPresentation({
      aspectRatio: imageAspectRatio,
      frameAspectRatio: cardAspectRatio,
      framePreset: nextFramePreset,
      surface: nextSurface,
      focusX: nextContentFit.focusX,
      focusY: nextContentFit.focusY,
      contentScale: nextContentFit.contentScale,
      fitMode: getProductCardFitMode({
        surface: nextSurface,
      }),
    });
  };

  const priceDisplay = getProductPriceDisplay(product);
  const mediaFrameClassName = [
    "product-image-box",
    "product-card-media-frame",
    `product-card-media-frame--${isVideo(primaryMedia) ? DEFAULT_MEDIA_FRAME_PRESET : mediaPresentation.framePreset}`,
    "product-card-media-frame--full-bleed",
  ].join(" ");
  const mediaFitMode = mediaPresentation.fitMode || DEFAULT_MEDIA_FIT_MODE;
  const mediaStyle = {
    objectPosition: `${mediaPresentation.focusX || DEFAULT_MEDIA_FOCUS_POINT.x}% ${mediaPresentation.focusY || DEFAULT_MEDIA_FOCUS_POINT.y}%`,
    transform: "none",
    transformOrigin: "center center",
  };
  const displayTitle = getProductDisplayTitle(product);
  const openProductDetail = () => {
    saveListingReturnPosition(location, {
      productId: product._id,
      cardElement: cardRef.current,
    });
    navigate(`/product/${product._id}`);
  };

  return (
    <div
      ref={cardRef}
      className="product-card-container product-card"
      data-store-product-id={product._id}
      onClick={openProductDetail}
    >
      <div
        className="wishlist-icon"
        onClick={(event) => {
          event.stopPropagation();

          if (inWishlist) {
            removeFromWishlist(product._id);
            setInWishlist(false);
            return;
          }

          const success = addToWishlist(product);

          if (success) {
            setInWishlist(true);
          }
        }}
      >
        {inWishlist ? <FaHeart className="heart-filled" /> : <FaRegHeart />}
      </div>

      <div className={mediaFrameClassName}>
        {isVideo(primaryMedia) ? (
          <div className="video-thumbnail-wrapper">
            <video
              ref={videoRef}
              src={primaryMedia}
              className="product-video-thumbnail"
              muted
              onMouseEnter={(event) => {
                videoRef.current = event.target;
                safePlayVideo(event.target);
              }}
              onMouseLeave={(event) => {
                safePauseVideo(event.target);

                if (videoRef.current === event.target) {
                  videoRef.current = null;
                }
              }}
              onError={(event) => {
                console.log("Video error:", event.target.error);
              }}
            />
            <div className="video-play-indicator">Video</div>
          </div>
        ) : primaryMedia ? (
          <img
            className={`product-card-media product-card-media--${mediaFitMode}`}
            src={primaryMedia}
            alt={displayTitle}
            loading="lazy"
            decoding="async"
            draggable={false}
            onLoad={handlePrimaryMediaLoad}
            style={mediaStyle}
          />
        ) : (
          <div className="product-image-placeholder">No Image</div>
        )}
      </div>

      <div className="product-info">
        <div className="product-meta-row">
          <h3 className="product-name">{displayTitle}</h3>

          <div className="product-pricing">
            <span className="discounted-pricee">{formatInr(priceDisplay.customerPrice)}</span>
            {priceDisplay.hasDiscount ? (
              <span className="product-card-old-price">{formatInr(priceDisplay.sellingPrice)}</span>
            ) : null}
          </div>
        </div>

        <button
          className="product-card-view-button"
          onClick={(event) => {
            event.stopPropagation();
            openProductDetail();
          }}
        >
          View Product
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
